import { createContext, useContext, useState, ReactNode, useCallback } from "react";
import { useNavigate } from "react-router-dom";

interface ImpersonatedAccount {
  accountId: string;
  accountName: string;
}

interface AdminImpersonationContextType {
  impersonatedAccount: ImpersonatedAccount | null;
  startImpersonation: (accountId: string, accountName: string) => void;
  stopImpersonation: () => void;
  isImpersonating: boolean;
}

const AdminImpersonationContext = createContext<AdminImpersonationContextType | undefined>(undefined);

export function AdminImpersonationProvider({ children }: { children: ReactNode }) {
  const [impersonatedAccount, setImpersonatedAccount] = useState<ImpersonatedAccount | null>(null);

  const startImpersonation = useCallback((accountId: string, accountName: string) => {
    setImpersonatedAccount({ accountId, accountName });
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatedAccount(null);
  }, []);

  return (
    <AdminImpersonationContext.Provider
      value={{
        impersonatedAccount,
        startImpersonation,
        stopImpersonation,
        isImpersonating: !!impersonatedAccount,
      }}
    >
      {children}
    </AdminImpersonationContext.Provider>
  );
}

export function useAdminImpersonation() {
  const context = useContext(AdminImpersonationContext);
  if (context === undefined) {
    throw new Error("useAdminImpersonation must be used within an AdminImpersonationProvider");
  }
  return context;
}
